import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { invalidationIntents, queryKeys } from "~/api/cache"

import type { SolarProposalAuroraDto } from "../dto/solar-proposal-aurora.dto"
import { solarProposalAuroraService } from "../services/solar-proposal-aurora.service"

/** Re-pull the active Aurora design, or switch to (or link) another version. */
export type SolarAuroraCommand =
  | { kind: `refresh`; proposalId: string }
  | { kind: `switch`; proposalId: string; auroraDesignId: string }

/**
 * Solar Proposal V2's Aurora refresh and version switch. Each runs inline at
 * the gateway and answers the updated Aurora read; on success every read the
 * design feeds is invalidated, so pricing and savings show out of date. A
 * failure refetches the Aurora read, since a lock may be why it failed.
 */
export default function useSolarAuroraCommand(): UseMutationResult<
  SolarProposalAuroraDto,
  unknown,
  SolarAuroraCommand
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (command: SolarAuroraCommand) =>
      command.kind === `refresh`
        ? solarProposalAuroraService.postRefresh(command.proposalId)
        : solarProposalAuroraService.postVersion(
            command.proposalId,
            command.auroraDesignId,
          ),
    onSuccess: (_saved, { proposalId }) =>
      invalidationIntents.solarAuroraDesignChanged(queryClient, proposalId),
    onError: (_error, { proposalId }) =>
      // Exact: the project listing is an Aurora call and didn't change.
      queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.aurora(proposalId),
        exact: true,
      }),
  })
}
